import { Dispatch } from 'redux';

import * as Actions from '../actions/questionnaire';
import ReplaceStyleKeywords from '../utils/ReplaceStyleKeywords';
import AbstractScenario from './AbstractScenario';

/** eラーニングアンケート表示情報取得シナリオ用パラメータ */
export interface IFetchQuestionnaireParam {
	careerID: string;
	classID: string;
	enqueteID: string;
	userId: string;
	isPreview: boolean;
}

/** eラーニングアンケート提出シナリオ用パラメータ */
export interface IUpdateQuestionnaireParam {
	careerID: string;
	classID: string;
	enqueteID: string;
	userId: string;
	startTime: string;
	renewalDate: string;
	blocks: Actions.IQuestionnaireBlock[];
	isPreview: boolean;
}

/** eラーニングアンケート一時保存シナリオ用パラメータ */
export type ISaveQuestionnaireParam = IUpdateQuestionnaireParam;

/**
 * アンケートシナリオファクトリ
 */
export default class QuestionnaireScenarioFactory {
	/**
	 * eラーニングアンケート表示情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchQuestionnaire(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IFetchQuestionnaireParam
		> {
			protected process(arg: IFetchQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					objectUserId: arg.userId,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read(
					'el_enquete_EnqueteFormBlockModel',
					param
				).then(data => {
					const displayTmpSaveBtn: boolean = data.displayTmpSaveBtn;
					const enqueteName: string = data.enqueteName;
					const startTime: string = data.startTime;
					const renewalDate: string = data.renewalDate;
					const periodJudgment: number = data.periodJudgment;
					const blocks = data.blockInfoList.map((b: any) =>
						QuestionnaireScenarioFactory.toBlock(b)
					);
					const scoreItemsRegisterFlag: string =
						data.scoreItemsRegisterFlag;
					const respondentCategoryFlg: string =
						data.respondentCategoryFlg;
					const mustAnswerFlg: number = data.mustAnswerFlg;
					dispatch(
						Actions.setQuestionnaireParams({
							blocks,
							displayTmpSaveBtn,
							enqueteName,
							mustAnswerFlg,
							periodJudgment,
							renewalDate,
							respondentCategoryFlg,
							scoreItemsRegisterFlag,
							startTime,
						})
					);
				});
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングアンケート提出シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createUpdateQuestionnaire(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IUpdateQuestionnaireParam
		> {
			protected process(arg: IUpdateQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerer: '1',
					blockInfoList: arg.blocks.map(b =>
						QuestionnaireScenarioFactory.makeBlockParam(b)
					),
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					enqueteSaveType: 1,
					mustAnswerFlg: 0,
					objectUserId: arg.userId,
					periodJudgment: 1,
					renewalDate: arg.renewalDate,
					saveType: 1,
					startTime: arg.startTime,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update('el_enquete_EnqueteFormBlockModel', param);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングアンケート一時保存シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createSaveQuestionnaire(dispatch: Dispatch, token: string) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			ISaveQuestionnaireParam
		> {
			protected process(arg: ISaveQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerer: '1',
					blockInfoList: arg.blocks.map(b =>
						QuestionnaireScenarioFactory.makeBlockParam(b)
					),
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					enqueteSaveType: 0,
					mustAnswerFlg: 0,
					objectUserId: arg.userId,
					periodJudgment: 1,
					renewalDate: arg.renewalDate,
					saveType: 0,
					startTime: arg.startTime,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update('el_enquete_EnqueteFormBlockModel', param);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニング表形式アンケート表示情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchQuestionnaireGrid(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IFetchQuestionnaireParam
		> {
			protected process(arg: IFetchQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					objectUserId: arg.userId,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read(
					'el_enquete_EnqueteGridFormBlockModel',
					param
				).then(data => {
					const displayTmpSaveBtn: boolean = data.displayTmpSaveBtn;
					const enqueteName: string = data.enqueteName;
					const startTime: string = data.startTime;
					const renewalDate: string = data.renewalDate;
					const periodJudgment: number = data.periodJudgment;
					const blocks = data.blockInfoList.map((b: any) =>
						QuestionnaireScenarioFactory.toBlockGrid(b)
					);
					const scoreItemsRegisterFlag: string =
						data.scoreItemsRegisterFlag;
					const respondentCategoryFlg: string =
						data.respondentCategoryFlg;
					const mustAnswerFlg: number = data.mustAnswerFlg;
					dispatch(
						Actions.setQuestionnaireParams({
							blocks,
							displayTmpSaveBtn,
							enqueteName,
							mustAnswerFlg,
							periodJudgment,
							renewalDate,
							respondentCategoryFlg,
							scoreItemsRegisterFlag,
							startTime,
						})
					);
					const options = data.replyList.map((r: any) =>
						QuestionnaireScenarioFactory.toOptionGrid(r)
					);
					dispatch(Actions.setQuestionnaireGridOptions({ options }));
				});
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニング表形式アンケート提出シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createUpdateQuestionnaireGrid(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IUpdateQuestionnaireParam
		> {
			protected process(arg: IUpdateQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerer: '1',
					blockInfoList: arg.blocks.map(b =>
						QuestionnaireScenarioFactory.makeBlockParam(b)
					),
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					enqueteSaveType: 1,
					mustAnswerFlg: 0,
					objectUserId: arg.userId,
					periodJudgment: 1,
					renewalDate: arg.renewalDate,
					saveType: 1,
					startTime: arg.startTime,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update(
					'el_enquete_EnqueteGridFormBlockModel',
					param
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニング表形式アンケート一時保存シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createSaveQuestionnaireGrid(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			ISaveQuestionnaireParam
		> {
			protected process(arg: ISaveQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerer: '1',
					blockInfoList: arg.blocks.map(b =>
						QuestionnaireScenarioFactory.makeBlockParam(b)
					),
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					enqueteSaveType: 0,
					mustAnswerFlg: 0,
					objectUserId: arg.userId,
					periodJudgment: 1,
					renewalDate: arg.renewalDate,
					saveType: 0,
					startTime: arg.startTime,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update(
					'el_enquete_EnqueteGridFormBlockModel',
					param
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングウィザード形式アンケート表示情報取得シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createFetchQuestionnaireWizard(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IFetchQuestionnaireParam
		> {
			protected process(arg: IFetchQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					objectUserId: arg.userId,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.read(
					'el_enquete_EnqueteWizardFormBlockModel',
					param
				).then(data => {
					const displayTmpSaveBtn: boolean = data.displayTmpSaveBtn;
					const enqueteName: string = data.enqueteName;
					const startTime: string = data.startTime;
					const renewalDate: string = data.renewalDate;
					const periodJudgment: number = data.periodJudgment;
					const blocks = data.blockInfoList.map((b: any) =>
						QuestionnaireScenarioFactory.toBlock(b)
					);
					const scoreItemsRegisterFlag: string =
						data.scoreItemsRegisterFlag;
					const respondentCategoryFlg: string =
						data.respondentCategoryFlg;
					const mustAnswerFlg: number = data.mustAnswerFlg;
					dispatch(
						Actions.setQuestionnaireParams({
							blocks,
							displayTmpSaveBtn,
							enqueteName,
							mustAnswerFlg,
							periodJudgment,
							renewalDate,
							respondentCategoryFlg,
							scoreItemsRegisterFlag,
							startTime,
						})
					);
				});
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングウィザード形式アンケート提出シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createUpdateQuestionnaireWizard(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			IUpdateQuestionnaireParam
		> {
			protected process(arg: IUpdateQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerer: '1',
					blockInfoList: arg.blocks.map(b =>
						QuestionnaireScenarioFactory.makeBlockParam(b)
					),
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					enqueteSaveType: 1,
					mustAnswerFlg: 0,
					objectUserId: arg.userId,
					periodJudgment: 1,
					renewalDate: arg.renewalDate,
					saveType: 1,
					startTime: arg.startTime,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update(
					'el_enquete_EnqueteWizardFormBlockModel',
					param
				);
			}
		}(dispatch, token);
		return scenario;
	}

	/**
	 * eラーニングウィザード形式アンケート一時保存シナリオ作成
	 * @param dispatch
	 * @param token
	 */
	public static createSaveQuestionnaireWizard(
		dispatch: Dispatch,
		token: string
	) {
		// tslint:disable-next-line:max-classes-per-file
		const scenario = new class extends AbstractScenario<
			ISaveQuestionnaireParam
		> {
			protected process(arg: ISaveQuestionnaireParam) {
				const param = {
					accessType: arg.isPreview ? 21 : 10,
					answerer: '1',
					blockInfoList: arg.blocks.map(b =>
						QuestionnaireScenarioFactory.makeBlockParam(b)
					),
					careerID: arg.careerID,
					classID: arg.classID,
					enqueteID: arg.enqueteID,
					enqueteSaveType: 0,
					mustAnswerFlg: 0,
					objectUserId: arg.userId,
					periodJudgment: 1,
					renewalDate: arg.renewalDate,
					saveType: 0,
					startTime: arg.startTime,
					userId: arg.userId,
					viewMode: arg.isPreview ? 1 : 0,
				};
				return this.update(
					'el_enquete_EnqueteWizardFormBlockModel',
					param
				);
			}
		}(dispatch, token);
		return scenario;
	}

	protected static toBlock(from: any) {
		const to: Actions.IQuestionnaireBlock = {
			blockID: from.blockID,
			blockName: from.blockName,
			blockNo: from.blockNo,
			questionnaires: from.questionnaireInfoList.map((q: any) =>
				this.toQuestionnaire(q)
			),
		};
		return to;
	}

	protected static toQuestionnaire(from: any) {
		let list: any;
		const questionnaireType: string = from.questionnaireType;
		let options: Actions.IOption[] | null = null;
		let selectedReply = 0;
		switch (questionnaireType) {
			case Actions.QuestionnaireType.SELECT:
				list = from.selectReplyInfoList;
				if (list && list.length > 0 && list[0].replyList) {
					options = list[0].replyList.map((r: any) => ({
						replyOrder: r.replyOrder,
						replyText: r.replyText,
					}));
					selectedReply = +list[0].selectedReply;
				}
				break;
			case Actions.QuestionnaireType.RADIO_BUTTON:
				list = from.radioReplyInfoList;
				if (list && list.length > 0 && list[0].replyList) {
					options = list[0].replyList.map((r: any) => ({
						replyOrder: r.replyOrder,
						replyText: r.replyText,
					}));
					selectedReply = +list[0].selectedReply;
				}
				break;
			case Actions.QuestionnaireType.CHECK_BOX:
				list = from.checkReplyInfoList;
				if (list) {
					options = list.map((r: any) => ({
						replyOrder: r.replyOrder,
						replyText: r.replyText,
						selected: r.selected,
					}));
				}
				break;
			case Actions.QuestionnaireType.TEXT_AREA:
				list = from.textReplyInfoList;
				if (list) {
					options = list.map((r: any) => ({
						replyOrder: r.replyOrder,
						replyText: r.replyText,
					}));
				}
				break;
		}
		const to: Actions.IQuestionnaire = {
			mustFlg: from.mustFlg,
			options,
			questionnaireID: from.questionnaireID,
			questionnaireNo: from.questionnaireNo,
			questionnaireText: ReplaceStyleKeywords.replaceStyleKeywords(
				from.questionnaireText
			),
			questionnaireType,
			selectedReply,
		};
		return to;
	}

	protected static makeBlockParam(block: Actions.IQuestionnaireBlock) {
		return {
			blockID: block.blockID,
			blockName: block.blockName,
			blockNo: block.blockNo,
			questionnaireInfoList: block.questionnaires.map(q =>
				this.makeQuestionnaireParam(q)
			),
		};
	}

	protected static makeQuestionnaireParam(
		questionnaire: Actions.IQuestionnaire
	) {
		const param: any = {
			questionnaireID: questionnaire.questionnaireID,
			questionnaireNo: questionnaire.questionnaireNo,
			questionnaireText: questionnaire.questionnaireText,
			questionnaireType: questionnaire.questionnaireType,
		};
		switch (questionnaire.questionnaireType) {
			case Actions.QuestionnaireType.SELECT:
				param.selectReplyInfoList = [
					{
						selectedReply: questionnaire.selectedReply,
					},
				];
				break;
			case Actions.QuestionnaireType.RADIO_BUTTON:
				param.radioReplyInfoList = [
					{
						selectedReply: questionnaire.selectedReply,
					},
				];
				break;
			case Actions.QuestionnaireType.CHECK_BOX:
				param.checkReplyInfoList = [];
				for (const o of questionnaire.options as Actions.IOption[]) {
					param.checkReplyInfoList.push({
						replyOrder: o.replyOrder,
						selected: o.selected,
					});
				}
				break;
			case Actions.QuestionnaireType.TEXT_AREA:
				if (questionnaire.options) {
					param.textReplyInfoList = [
						{
							replyText: questionnaire.options[0].replyText,
						},
					];
				}
				break;
			case undefined:
				param.selectedReply = questionnaire.selectedReply;
				break;
		}

		return param;
	}

	protected static toBlockGrid(from: any) {
		const to: Actions.IQuestionnaireBlock = {
			blockID: from.blockID,
			blockName: from.blockName,
			questionnaires: from.questionnaireInfoList.map((q: any) =>
				this.toQuestionnaireGrid(q)
			),
		};
		return to;
	}

	protected static toQuestionnaireGrid(from: any) {
		const to: Actions.IQuestionnaire = {
			mustFlg: from.mustFlg,
			questionnaireID: from.questionnaireID,
			questionnaireNo: from.questionnaireNo,
			questionnaireText: ReplaceStyleKeywords.replaceStyleKeywords(
				from.questionnaireText
			),
			selectedReply: +from.selectedReply,
		};
		return to;
	}

	protected static toOptionGrid(from: any) {
		const to: Actions.IOption = {
			replyOrder: from.replyOrder,
			replyText: from.replyText,
		};
		return to;
	}
}



// WEBPACK FOOTER //
// ./src/scenario/QuestionnaireScenarioFactory.ts